import { gql, useQuery } from "@apollo/client";
import { getFetchPolicyOptions } from "api/utils";
import {
  PORTFOLIO_REPORT_HOLDINGS_DETAILS_FIELDS,
  PORTFOLIO_REPORT_UNPAID_TRANSACTIONS_FIELDS,
} from "./fragments";
import {
  HoldingPosition,
  PortfolioHoldingDetailsQuery,
  UnpaidTrades,
} from "./types";

/*
const HOLDING_DETAILS_QUERY = gql`

  query GetPortfolioHoldingsFromPfReport($portfolioId: Long) {
    portfolio(id: $portfolioId) {
      id
      portfolioReport (calculateExpectedAmountBasedOpenTradeOrders: true) {
        portfolioId
        unpaidTransactions {
          id
          security{
            id
          }
          typeCode
          typeName
          amount
          tradeAmount
        }
      }
    }
  }
`;
*/

const HOLDING_DETAILS_QUERY = gql`
  ${PORTFOLIO_REPORT_HOLDINGS_DETAILS_FIELDS}
  ${PORTFOLIO_REPORT_UNPAID_TRANSACTIONS_FIELDS}

  query GetPortfolioHoldingsFromPfReport($portfolioId: Long) {
    portfolio(id: $portfolioId) {
      id
      portfolioReport(calculateExpectedAmountBasedOpenTradeOrders: true) {
        portfolioId
        ...PortfolioReportHoldingDetailsFields
      }
      unpaidPortfolioReport: portfolioReport(
        calculateExpectedAmountBasedOpenTradeOrders: true
      ) {
        portfolioId
        ...PortfolioReportUnpaidTransactionsFields
      }
    }
  }
`;

/**
 * Gets all holdings of a portfolio from portfolioReport and then finds the
 * holding of the provided security id and returns it. The benefit of using this
 * is that portfolioReport has more up-to-date figures than analytics+.
 * @param portfolioId the portfolio to get holdings from
 * @param securityId the id of the holding to get from the portfolio
 * @returns the holding in the portfolio
 */
export const useGetPortfolioHoldingFromPfReport = (
  portfolioId: number | undefined,
  securityId: string | undefined
) => {
  const { loading, error, data } = useQuery<PortfolioHoldingDetailsQuery>(
    HOLDING_DETAILS_QUERY,
    {
      variables: {
        portfolioId,
      },
      skip: !portfolioId || !securityId,
      ...getFetchPolicyOptions(`useGetPortfolioHoldingDetails.${portfolioId}`),
    }
  );

  const [netTradeAmountSold, netAmountSold] = getUnpaidTransactions(
    data?.portfolio?.unpaidPortfolioReport.unpaidTransactions,
    securityId
  );

  return {
    loading,
    error,
    data: findHolding(
      data?.portfolio?.portfolioReport?.holdingPositions,
      securityId
    ),
    netSoldCash: netTradeAmountSold,
    netSoldAmount: netAmountSold,
  };
};

/**
 * @returns the holding matching the id and amount not 0.
 */
const findHolding = (
  holdingPositions: HoldingPosition[] | undefined,
  securityId: string | undefined
) => {
  return holdingPositions?.find(
    (holding) =>
      holding.security?.id.toString() === securityId && holding.amount !== 0
  );
};

const getUnpaidTransactions = (
  unpaidTrades: UnpaidTrades[] | undefined,
  securityId: string | undefined
) => {
  const securityTrades = unpaidTrades?.filter(
    (trade) => trade.security?.id.toString() === securityId
  );
  const tradeAmounts = securityTrades?.map((trade) =>
    trade.type.amountEffect === -1 ? -trade.tradeAmount : 0
  );
  const amounts = securityTrades?.map((trade) =>
    trade.type.amountEffect === -1 ? -trade.amount : 0
  );
  return [
    tradeAmounts?.reduce((total, trade) => total + trade, 0) || 0,
    amounts?.reduce((total, trade) => total + trade, 0) || 0,
  ];
};
